/** Normalize query string for stable comparison (`?b=1&a=2` vs `?a=2&b=1`). */
function normalizeSearch(search) {
  const raw = String(search || '').trim();
  if (!raw) return '';
  const q = raw.startsWith('?') ? raw.slice(1) : raw;
  if (!q) return '';
  const params = new URLSearchParams(q);
  const parts = [];
  [...params.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([key, val]) => parts.push(`${key}=${val}`));
  return parts.length ? `?${parts.join('&')}` : '';
}

/** True when the user is already on this exact nav target (path + query; not parent/child paths). */
export function isSameNavRoute(pathname, to, { end = false, search = '', currentSearch = '' } = {}) {
  const targetSearch = normalizeSearch(search);
  const activeSearch = normalizeSearch(currentSearch);
  if (end || to === '/') {
    return pathname === to && targetSearch === activeSearch;
  }
  if (pathname !== to) return false;
  return targetSearch === activeSearch;
}

/** Parse internal link target path + search from href. */
export function parseInternalNavTarget(raw, origin = window.location.origin) {
  try {
    const url = new URL(raw, origin);
    return { pathname: url.pathname, search: url.search };
  } catch {
    const [pathPart, rest = ''] = raw.split('?');
    const path = pathPart.split('#')[0];
    const search = rest ? `?${rest.split('#')[0]}` : '';
    return { pathname: path, search };
  }
}

export function scrollToPageTop({ smooth = false } = {}) {
  const behavior = smooth ? 'smooth' : 'auto';
  window.scrollTo({ top: 0, left: 0, behavior });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

/** Scroll to top after route change — retries after mobile nav collapse and layout paint. */
export function scheduleScrollToPageTop({ smooth = false } = {}) {
  const run = () => scrollToPageTop({ smooth });

  run();

  const nav = document.getElementById('navbarNav');
  if (nav?.classList.contains('show')) {
    nav.addEventListener('hidden.bs.collapse', run, { once: true });
  }

  let rafId = window.requestAnimationFrame(() => {
    rafId = window.requestAnimationFrame(run);
  });
  const timeoutId = window.setTimeout(run, 180);

  return () => {
    window.cancelAnimationFrame(rafId);
    window.clearTimeout(timeoutId);
    nav?.removeEventListener('hidden.bs.collapse', run);
  };
}

export function isMobileNavOpen() {
  return document.getElementById('navbarNav')?.classList.contains('show') ?? false;
}

let shopMobileNavCloseCallback = null;

/** ShopMobileNav registers a React setter — reliable vs nested Bootstrap collapse. */
export function registerShopMobileNavClose(callback) {
  shopMobileNavCloseCallback = callback;
  return () => {
    if (shopMobileNavCloseCallback === callback) {
      shopMobileNavCloseCallback = null;
    }
  };
}

function hideBootstrapCollapse(elementId) {
  const el = document.getElementById(elementId);
  if (!el) return;
  const Collapse = typeof window !== 'undefined' ? window.bootstrap?.Collapse : null;
  if (Collapse) {
    try {
      const instance = Collapse.getInstance(el) || Collapse.getOrCreateInstance(el, { toggle: false });
      instance.hide();
    } catch {
      /* fall through to DOM cleanup */
    }
  }
  el.classList.remove('show', 'collapsing');
  if (!el.classList.contains('collapse')) {
    el.classList.add('collapse');
  }
  el.style.height = '';
}

function resetShopMobileNavToggle() {
  const toggle = document.querySelector('.shop-mobile-nav__toggle[data-bs-target="#mobileShopNav"]');
  if (!toggle) return;
  toggle.setAttribute('aria-expanded', 'false');
  toggle.classList.add('collapsed');
}

export function isShopSectionPath(pathname) {
  const path = String(pathname || '');
  return (
    path === '/shop' ||
    path.startsWith('/shop/') ||
    path.startsWith('/gift-finder') ||
    path.startsWith('/custom-jewelry') ||
    path.startsWith('/product/')
  );
}

/** Collapse nested Shop accordion inside the mobile menu. */
export function closeShopMobileNav() {
  shopMobileNavCloseCallback?.();
  hideBootstrapCollapse('mobileShopNav');
  resetShopMobileNavToggle();
}

/** Close the mobile drawer without toggling (avoids swallowing the link click). */
export function closeMobileNav() {
  closeShopMobileNav();
  const nav = document.getElementById('navbarNav');
  if (!nav?.classList.contains('show')) return;
  const Collapse = typeof window !== 'undefined' ? window.bootstrap?.Collapse : null;
  if (Collapse) {
    Collapse.getOrCreateInstance(nav, { toggle: false }).hide();
    return;
  }
  const toggler = document.querySelector('button.navbar-toggler[data-bs-target="#navbarNav"]');
  toggler?.click();
}

/**
 * Nav link click: always collapse Shop drawer section; close mobile menu when open.
 * Re-tap current route: also scroll to top (no full reload).
 */
export function createSameRouteNavHandler(pathname, { onBefore, smooth = true, currentSearch = '' } = {}) {
  return (to, { end = false, search = '' } = {}) => (e) => {
    closeShopMobileNav();

    if (isSameNavRoute(pathname, to, { end, search, currentSearch })) {
      onBefore?.();
      e.preventDefault();
      scheduleScrollToPageTop({ smooth });
      return;
    }

    if (isMobileNavOpen()) {
      onBefore?.();
    }
  };
}
